import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Collapse,
    Button,
    Divider,
} from '@mui/material';

const RecentlySubmitted = () => {
    const [submissions, setSubmissions] = useState([]);
    const [expandedIds, setExpandedIds] = useState({});

    useEffect(() => {
        // ✅ FIX: Add a unique query parameter to the URL to prevent caching.
        // `_=` is a common convention, and `Date.now()` makes it unique on every fetch.
        const url = `https://api.admin.marsreel.com/api/preferences/recent?_=${Date.now()}`;

        fetch(url) // Use the new cache-busting URL
            .then(res => res.json())
            .then((data) => {
                const entriesWithIds = data.map((entry, index) => ({
                    ...entry,
                    id: index + 1, // Using index as a key is okay for a stable, non-reordering list
                    summary: generateSummary(entry),
                }));
                setSubmissions(entriesWithIds);
            })
            .catch((err) => {
                console.error('Failed to fetch recent preferences:', err);
            });
    }, []); // This useEffect still only runs once on component mount, which is correct.

    const toggleExpanded = (id) => {
        setExpandedIds((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const convertToLocalTime = (timeStr) => {
        if (!timeStr) return '';
        const [hour, minute] = timeStr.split(':').map(Number);
        const localDate = new Date();
        localDate.setHours(hour);
        localDate.setMinutes(minute);
        localDate.setSeconds(0);
        localDate.setMilliseconds(0);

        return localDate.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
        });
    };


    const generateSummary = (entry) => {
        const { preferredDays = [], timeRanges = [], city, zip } = entry;

        const labels = [...new Set(timeRanges.map(t => t.label))];
        const timeText = labels.length === 5 ? 'all-day' : labels.join(', ').toLowerCase();
        const dayText = preferredDays.length === 7 ? 'all week' : preferredDays.join(', ');
        const cityText = city && city !== 'N/A' ? ` in ${city}` : ' in their area';
        const zipText = zip && zip !== 'N/A' ? ` (Zip ${zip})` : '';

        return `Prefers ${timeText} training on ${dayText}${cityText}${zipText}.`;
    };

    return (
        <Box sx={{ mt: 6 }}>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                Recently Submitted Preferences
            </Typography>

            <Box
                sx={{
                    maxHeight: 360,
                    overflowY: 'auto',
                    backgroundColor: '#1a1a1a',
                    borderRadius: 2,
                    p: 2,
                }}
            >
                {submissions.map((entry) => (
                    <Paper
                        key={entry.id}
                        sx={{
                            backgroundColor: '#222',
                            p: 2,
                            mb: 2,
                            borderLeft: '4px solid #12c099',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                            {entry.submittedAgo}: {entry.name} submitted preferences
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                            {entry.summary}
                        </Typography>

                        <Button
                            size="small"
                            onClick={() => toggleExpanded(entry.id)}
                            sx={{ color: '#12c099', textTransform: 'none', fontSize: '0.85rem' }}
                        >
                            {expandedIds[entry.id] ? 'Hide Details' : 'Show Details'}
                        </Button>

                        <Collapse in={expandedIds[entry.id]}>
                            <Divider sx={{ my: 1, borderColor: '#333' }} />
                            <Box sx={{ pl: 1 }}>
                                <Typography sx={{ color: '#bbb', fontSize: '0.85rem' }}>
                                    <strong>Days Available:</strong> {entry.preferredDays.join(', ')}
                                </Typography>
                                <Typography sx={{ color: '#bbb', fontSize: '0.85rem' }}>
                                    <strong>Time Ranges:</strong>{' '}
                                    {entry.timeRanges
                                        .map((tr) => {
                                            const startLocal = convertToLocalTime(tr.start);
                                            const endLocal = convertToLocalTime(tr.end);
                                            return `${startLocal} - ${endLocal} (${tr.label})`;
                                        })
                                        .join(', ')}
                                </Typography>
                                <Typography sx={{ color: '#bbb', fontSize: '0.85rem' }}>
                                    <strong>City:</strong> {entry.city || 'N/A'}
                                </Typography>
                                <Typography sx={{ color: '#bbb', fontSize: '0.85rem' }}>
                                    <strong>Zip Code:</strong> {entry.zip || 'N/A'}
                                </Typography>
                                <Typography sx={{ color: '#bbb', fontSize: '0.85rem' }}>
                                    <strong>Source:</strong> {entry.origin || 'N/A'}
                                </Typography>
                                {entry.notes && (
                                    <Typography sx={{ color: '#bbb', fontSize: '0.85rem' }}>
                                        <strong>Notes:</strong> {entry.notes}
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                ))}
            </Box>
        </Box>
    );
};

export default RecentlySubmitted;
